import React, { useRef, MouseEvent } from 'react';
import styled from 'styled-components/macro';
import { ResourceStrings } from '../../ResourceStrings';
import { showIf } from '../../utils/react';
import Button from './Button';
import { CropRect } from '../../CropRect';
import { App } from '../../app';

export interface ImageEditorProps {
    imagePath: string;
    altText?: string;
    isCropping: boolean;
    cropRegion?: CropRect;
    disabled?: boolean;
    onCropClicked: () => void;
    onAcceptCropClicked: (cropRect: CropRect) => void;
    onCancelCropClicked: () => void;
    postBusinessEvent?: (eventName: string, extraProperties?: { [key: string]: string }) => void;
}

const getClientOffset = (elem: HTMLElement): { top: number; left: number } => { // crossbrowser version
    const box = elem.getBoundingClientRect();

    const body = document.body;
    const docEl = document.documentElement;

    // // const scrollTop = window.pageYOffset || docEl.scrollTop || body.scrollTop;
    // // const scrollLeft = window.pageXOffset || docEl.scrollLeft || body.scrollLeft;

    const clientTop = docEl.clientTop || body.clientTop || 0;
    const clientLeft = docEl.clientLeft || body.clientLeft || 0;

    const top = box.top - clientTop;
    const left = box.left - clientLeft;
    //// Using scroll offset was causing mouse crop handle to be offset by scroll position incorrectly. Removing it work well
    // // const top = box.top + scrollTop - clientTop;
    // // const left = box.left + scrollLeft - clientLeft;

    return { top: Math.round(top), left: Math.round(left) };
}

export default function ImageEditor(props: ImageEditorProps): JSX.Element {
    const refBtnCrop = useRef(null);
    const refCropImg = useRef(null);
    const refCropCover = useRef(null);
    const refCropHandle1 = useRef(null);
    const refCropHandle2 = useRef(null);

    const refCurrCropHandle = useRef(null);
    const refInitialWidth = useRef(0);
    const refInitialHeight = useRef(0);
    const refTotalWidth = useRef(0);
    const refTotalHeight = useRef(0);
    const refStartX = useRef(0);
    const refStartY = useRef(0);
    const refEndX = useRef(0);
    const refEndY = useRef(0);
    const refIsMouseDown = useRef(false);

    const getCropImg = (): HTMLElement => {
        const result: any = refCropImg.current;
        if (result instanceof Array) {
            return result[result.length - 1];
        }
        return result;
    }

    const document_keydown = (e: KeyboardEvent, isCropping: boolean): void => {
        if (isCropping) {
            return;
        }

        if (refCropImg.current == null) {
            return;
        }

        if ((document.activeElement !== refCropHandle1.current) && (document.activeElement !== refCropHandle2.current)) {
            return;
        }

        const UP_ARROW: number = 38;
        const DOWN_ARROW: number = 40;
        const LEFT_ARROW: number = 37;
        const RIGHT_ARROW: number = 39;
        const ENTER: number = 13;

        const OFFSET_REM: number = 1;
        let xOffset: number = 0;
        let yOffset: number = 0;
        switch (e.keyCode) {
            case UP_ARROW:
                yOffset = -OFFSET_REM;
                break;

            case DOWN_ARROW:
                yOffset = OFFSET_REM;
                break;

            case LEFT_ARROW:
                xOffset = -OFFSET_REM;
                break;

            case RIGHT_ARROW:
                xOffset = OFFSET_REM;
                break;

            case ENTER:
                e.preventDefault();
                e.stopPropagation();
                cropAcceptClick();
                return;

            default:
                return;
        }

        if (document.activeElement === refCropHandle1.current) {
            refStartX.current += xOffset;
            refStartY.current += yOffset;
            restrictCropStartPos();
            if (xOffset !== 0) {
                App.read(ResourceStrings.cropInfoLeft(Math.floor(refStartX.current * 1000 / refTotalWidth.current) / 10));
            }
            if (yOffset !== 0) {
                App.read(ResourceStrings.cropInfoTop(Math.floor(refStartY.current * 1000 / refTotalHeight.current) / 10));
            }
        } else {
            refEndX.current += xOffset;
            refEndY.current += yOffset;
            restrictCropEndPos();
            if (xOffset !== 0) {
                App.read(ResourceStrings.cropInfoWidth(Math.floor((refEndX.current - refStartX.current) * 1000 / refTotalWidth.current) / 10));
            }
            if (yOffset !== 0) {
                App.read(ResourceStrings.cropInfoHeight(Math.floor((refEndY.current - refStartY.current) * 1000 / refTotalHeight.current) / 10));
            }
        }

        e.preventDefault();
        updateCropCover();
    }

    const restrictCropStartPos = (): void => {
        if (refStartX.current < 0) {
            refStartX.current = 0;
        }

        if (refStartY.current < 0) {
            refStartY.current = 0;
        }

        if (refStartX.current > refEndX.current) {
            refStartX.current = refEndX.current;
        }

        if (refStartY.current > refEndY.current) {
            refStartY.current = refEndY.current;
        }
    }

    const restrictCropEndPos = (): void => {
        if (refEndX.current > refTotalWidth.current) {
            refEndX.current = refTotalWidth.current;
        }

        if (refEndY.current > refTotalHeight.current) {
            refEndY.current = refTotalHeight.current;
        }

        if (refEndX.current < refStartX.current) {
            refEndX.current = refStartX.current;
        }

        if (refEndY.current < refStartY.current) {
            refEndY.current = refStartY.current;
        }
    }

    const cropAcceptClick = (): void => {
        const cropRect = doCrop();
        props.onAcceptCropClicked(cropRect);
    }

    const cropCancelClick = (): void => {
        props.onCancelCropClicked();
    }

    const doCrop = (): CropRect => {
        if (refCropImg.current == null) { // if nothing to crop
            return null;
        }
        let rect = CropRect.FromUnsortedPositions(refStartX.current, refStartY.current, refEndX.current, refEndY.current, refTotalWidth.current, refTotalHeight.current);
        rect = CropRect.scale(rect, 1); // App.settings.computed_thumbWidth / this.totalWidth); // @TODO: replace with new alternative

        if ((rect.width() === 0) || (rect.height() === 0) ||
            isNaN(rect.width()) || isNaN(rect.height())) { // if nothing to crop
            return null;
        }

        return rect;
    }

    const documentMousemove = (e: MouseEvent): void => {
        if (!refIsMouseDown.current) {
            return;
        }

        if (refCropImg.current == null) {
            return;
        }

        const coords: { left: number; top: number } = getClientOffset(refCropImg.current);

        if (refCurrCropHandle.current === refCropHandle1.current) {
            refStartX.current = e.clientX - coords.left;
            refStartY.current = e.clientY - coords.top;
            restrictCropStartPos();
        } else {
            refEndX.current = e.clientX - coords.left;
            refEndY.current = e.clientY - coords.top;
            restrictCropEndPos();
        }

        e.preventDefault();
        updateCropCover();
    }

    const documentMouseup = (e: MouseEvent): void => {
        document.removeEventListener("mouseup", documentMouseup as any);
        document.removeEventListener("mousemove", documentMousemove as any);
        if (!refIsMouseDown.current) {
            return;
        }

        e.preventDefault();
        doMouseUpActions();
    }

    const crophandleMousedown = (e: MouseEvent): void => {
        refCurrCropHandle.current = e.target as any;
        if (refCurrCropHandle.current != null) {
            refCurrCropHandle.current.focus();
        }

        refIsMouseDown.current = true;
        document.addEventListener("mouseup", documentMouseup as any);
        document.addEventListener("mousemove", documentMousemove as any);

        e.preventDefault();
        // @TODO: Enable or remove
        // // Actions.setIsCropPromptVisible(false);
    }

    const crophandleMouseup = (e: MouseEvent): void => {
        if (!refIsMouseDown.current) {
            return;
        }
        e.preventDefault();
        doMouseUpActions();
    }

    const doMouseUpActions = (): void => {
        refIsMouseDown.current = false;
        updateCropCover();
    }

    const updateCropCover = (): void => {
        const cropCover: HTMLElement = refCropCover.current;

        const rect = CropRect.FromUnsortedPositions(refStartX.current, refStartY.current, refEndX.current, refEndY.current, refTotalWidth.current, refTotalHeight.current);
        rect.right = refTotalWidth.current - rect.right; // convert to margin (distance from edge)
        rect.bottom = refTotalHeight.current - rect.bottom; // convert to margin (distance from edge)

        const scrollX = window.scrollX;
        const scrollY = window.scrollY;
        cropCover.style.borderWidth = `${rect.top}px ${rect.right}px ${rect.bottom}px ${rect.left}px`;
        // Recent versions of chrome and edge seem to automatically scroll the page
        // in order to keep the selected crop handle at same position on screen when it is dragged.
        // This causes the page to scroll when user changes the crop region.
        // So reset the scroll position to what it was before the crop handle was dragged.
        window.scroll(scrollX, scrollY);
    }

    const resetCropCover = (): void => {
        const cropImg: HTMLElement = getCropImg();
        if (refInitialWidth.current === 0) {
            refInitialWidth.current = cropImg.offsetWidth;
        }
        if (refInitialHeight.current === 0) {
            refInitialHeight.current = cropImg.offsetHeight;
        }
        refTotalWidth.current = refInitialWidth.current;
        refTotalHeight.current = refInitialHeight.current;

        refStartX.current = refTotalWidth.current * 0.2;
        refStartY.current = refTotalHeight.current * 0.2;
        refEndX.current = refTotalWidth.current * 0.8;
        refEndY.current = refTotalHeight.current * 0.8;

        updateCropCover();
    }

    const cropClicked = (isCropping: boolean): void => {
        resetCropCover();

        props.onCropClicked();

        document.addEventListener("keydown", (e) => document_keydown(e, isCropping));

        requestAnimationFrame(() => {
            if (refCropHandle1.current != null) {
                refCropHandle1.current.focus();
            }
        });

        props.postBusinessEvent?.("crop-clicked");
    }

    return <Wrapper>
        <div className="crop-container-container">
            <div className="crop-container">
                <PreviewImage
                    ref={refCropImg}
                    data-cy="image-preview"
                    src={props.imagePath}
                    alt={props.altText}
                    className={!props.isCropping && "is-not-cropping"}
                    cropRegion={props.cropRegion} />

                <div ref={refCropCover} className="crop-cover" style={showIf(props.isCropping)}>
                    <div
                        ref={refCropHandle1}
                        data-cy="crop-handle-1"
                        className="crop-handle crop1"
                        role="button"
                        tabIndex={0}
                        onMouseDown={(evt) => crophandleMousedown(evt)}
                        onMouseUp={(evt) => crophandleMouseup(evt)}
                        aria-label={ResourceStrings.cropHandleTopLeft()}>
                        <div
                            className="crop-prompt">
                            {ResourceStrings.cropPrompt()}
                        </div>
                    </div>
                    <div
                        ref={refCropHandle2}
                        data-cy="crop-handle-2"
                        className="crop-handle crop2"
                        role="button"
                        tabIndex={0}
                        onMouseDown={(evt) => crophandleMousedown(evt)}
                        onMouseUp={(evt) => crophandleMouseup(evt)}
                        aria-label={ResourceStrings.cropHandleBottomRight()}>
                    </div>
                </div>
            </div>
        </div>
        <div className="btn-bar" css={"visibility: " + (props.disabled ? "hidden" : "visible")}>
            <Button
                ref={refBtnCrop}
                data-cy="crop-image"
                style={showIf(!props.isCropping)}
                className='crop-btn'
                buttonType='link'
                size='compact'
                onClick={() => cropClicked(props.isCropping)}>
                {ResourceStrings.cropScreenshot()}
            </Button>

            <Button
                data-cy="crop-accept"
                style={showIf(props.isCropping)}
                className='crop-btn'
                buttonType='link'
                size='compact'
                onClick={() => cropAcceptClick()}>
                {ResourceStrings.accept()}
            </Button>

            <Button
                style={showIf(props.isCropping)}
                data-cy="crop-cancel"
                className='crop-btn'
                buttonType='link'
                size='compact'
                onClick={() => cropCancelClick()}>
                {ResourceStrings.cancel()}
            </Button>
        </div>
    </Wrapper>
}

const Wrapper = styled.div`
    margin: 3.6rem 0 0 4rem;

    .crop-container-container {
        display: inline-block;
        margin-left: 1px;
        height: 20rem;

        img {
            display: block;
            max-width: none;
        }

        &:focus {
            outline: 0.1rem dashed black;
        }
    }

    .crop-container {
        display: inline-block;
        position: relative;
    }

    .crop-cover {
        position: absolute;
        border-width: 1rem 2rem 3rem 4rem;
        border-style: solid;
        border-color: rgba(90, 90, 90, 0.6);
        left: 0;
        top: 0;
        right: 0;
        bottom: 0;
    }

    .crop-prompt {
        position: absolute;
        left: 50%;
        background-color: rgba(0, 0, 0, 0.8);
        color: white;
        padding: 0.1rem 0.6rem;
        border-radius: 0.2rem;
        margin-right: -50%;
        transform: translate(-50%, 0);
        text-align: center;
        bottom: 1rem;
        min-width: 15rem;
    }

    .btn-bar {
        margin-top: 1rem;
    }

    .crop-btn {
            font-size: 1.6rem;
    }

    .crop-btn + .crop-btn {
            margin-left: 2rem;
    }

    .crop-handle {
        width: 1rem;
        height: 1rem;
        background: black;
        border: 0.2rem solid white;
        cursor: move;
    }

    .crop-handle.crop1 {
        position: absolute;
        top: -0.5rem;
        left: -0.5rem;
    }

    .crop-handle.crop2 {
        position: absolute;
        bottom: -0.5rem;
        right: -0.5rem;
    }

    .crop-handle:focus {
        background: white;
        border-color: #0576C7;
    }
`;

// @TODO: Correct preview region
const PreviewImage = styled.img<{ cropRegion: CropRect }>`
    &&.is-not-cropping {
        object-fit: none;
        object-position: ${props => props.cropRegion && `-${props.cropRegion.left}px -${props.cropRegion.top}px`};
        width: ${props => props.cropRegion && `${props.cropRegion.right - props.cropRegion.left}px`};
        height: ${props => props.cropRegion && `${props.cropRegion.bottom - props.cropRegion.top}px`};
    }
`;
